/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { HistoricalReportData, ReportData } from '../../../../types/reportData';
import { DATE, formatDateUTC } from '../../../../utils/date';
import { arrayToTableRow, heading4, paragraph, paragraphWithBoldText, spacer } from '../../document-structure';
import { getUnit, renderQuestionValue } from '../../questionView';
import { HEADER_ROW_STYLES, paddedTableCell, STYLES } from '../sgx-metric-report-generator';
import { AlignmentType, Paragraph, Table, TableRow, TextRun } from 'docx';
import { getName } from '../../../../utils/universalTracker';
import { SurveyModelMinimalUtrv, UnitConfig } from '../../../../model/surveyData';
import { getLatestNote, simpleTable } from '../../group-builder';
import { UtrValueType } from '../../../../types/universalTracker';
import { defaultPrivateQuestionText, shouldDisplayPrivateText } from '../../reportData';
import { VisibilityStatus } from '../../../../types/download';

type DataItem = Paragraph | Table;

export interface MultiDateRendererParams {
  /** Latest survey question **/
  currentData: ReportData;
  /** All available historical data **/
  questionData: HistoricalReportData[];

  targetMap: Map<string, SurveyModelMinimalUtrv>;

  preferredAltCodes?: string[];

  visibilityStatus: VisibilityStatus;

  /** Allow to still output empty questions and tables within with note **/
  showEmptyTables?: boolean;
  unitConfig?: UnitConfig;
  displayUserInput?: boolean;
}

const emptyTableText = 'Not provided';
const emptyNoteText = 'No further explanation provided.';
const latestExplainNotes = 'Latest Explain / Notes: ';

const generateDetails = (utrv: ReportData, targets: Map<string, SurveyModelMinimalUtrv>, visibility: VisibilityStatus) => {

  const noteHeader = utrv.effectiveDate ?
    `${formatDateUTC(utrv.effectiveDate, DATE.YEAR_ONLY)} ${latestExplainNotes}` :
    latestExplainNotes;

  const isPrivateText = shouldDisplayPrivateText(utrv, visibility);
  const latestNote = isPrivateText ? defaultPrivateQuestionText : getLatestNote(utrv, emptyNoteText);

  const additionalDetails = [paragraphWithBoldText(noteHeader, latestNote)]

  const target = targets.get(utrv.universalTracker._id);
  if (target?.value) {
    const unit = getUnit(utrv);
    const targetValue = isPrivateText ? defaultPrivateQuestionText
      : `${target.value}${unit} by ${formatDateUTC(target.effectiveDate, DATE.LONG)}`;
    additionalDetails.unshift(paragraphWithBoldText('Latest Target: ', targetValue));
  }
  return additionalDetails;
};


const getHeaderRowStyle = (cols: string[]) => {
  return cols.map(
    (t) =>
      new Paragraph({
        children: [
          new TextRun({
            text: t,
            color: 'FFFFFF',
            size: 24,
          }),
        ],
        alignment: AlignmentType.CENTER,
      })
  );
};

export const multiDateEntry = (params: MultiDateRendererParams): DataItem => {

  const { currentData: { universalTracker }, questionData, visibilityStatus, displayUserInput } = params

  const tableCells = questionData.map(qd => {
    const utrv = qd.reportData.find(d => d.universalTracker.code === universalTracker.code);
    if (!utrv) {
      return paddedTableCell(paragraph(''))
    }
    if (shouldDisplayPrivateText(utrv, visibilityStatus)) {
      return paddedTableCell(paragraph(defaultPrivateQuestionText))
    }

    const answer = renderQuestionValue(utrv, qd.unitConfig, HEADER_ROW_STYLES, {
      table: {
        width: STYLES.TABLE_WIDTH_LANDSCAPE,
        returnEmpty: false
      },
      displayUserInput,
    });

    if (answer instanceof Paragraph) {
      return paddedTableCell(answer)
    }

    return paddedTableCell(paragraph(''))
  })

  const dataCols = questionData.map((data) => formatDateUTC(data.effectiveDate, DATE.MONTH_YEAR_SHORT));

  return simpleTable([
    arrayToTableRow(getHeaderRowStyle(dataCols), HEADER_ROW_STYLES),
    new TableRow({ children: tableCells }),
  ], STYLES.TABLE_WIDTH_LANDSCAPE)
};


export const multiDateRenderer = (params: MultiDateRendererParams): DataItem[] => {

  const { currentData, questionData, targetMap, preferredAltCodes, visibilityStatus, displayUserInput } = params

  if (questionData.length === 0) {
    return []
  }

  const utr = currentData.universalTracker;
  const questionTitle = heading4(getName(utr, preferredAltCodes))

  if (utr.valueType !== UtrValueType.Table) {
    return [
      questionTitle,
      multiDateEntry(params),
      ...generateDetails(currentData, targetMap, visibilityStatus),
      spacer(),
    ]
  }

  const questionTables = questionData.map(qd => {
    const utrv = qd.reportData.find((item) => item.universalTracker.code === utr.code);
    if (!utrv) {
      return [];
    }

    const dateText = utrv.effectiveDate ? formatDateUTC(utrv.effectiveDate, DATE.MONTH_YEAR_SHORT) : '';
    const dateParagraph = paragraphWithBoldText('DATE: ', dateText);

    if (shouldDisplayPrivateText(utrv, visibilityStatus)) {
      return [dateParagraph, paragraph(defaultPrivateQuestionText), spacer()];
    }

    const answer = renderQuestionValue(utrv, qd.unitConfig, HEADER_ROW_STYLES, {
      table: {
        width: STYLES.TABLE_WIDTH_LANDSCAPE,
        returnEmpty: false
      },
      displayUserInput,
    });
    if (answer === undefined || answer === '') {
      return params.showEmptyTables ? [dateParagraph, paragraph(emptyTableText), spacer()] : [];
    }

    return [dateParagraph, answer, spacer()];
  }).flat();

  if (questionTables.length === 0 && !params.showEmptyTables) {
    return []
  }

  return [
    questionTitle,
    ...questionTables,
    ...generateDetails(currentData, targetMap, visibilityStatus),
    spacer(),
  ]
};


